/**
 * The Game Summary "General" column: the line score (FR-SUMMARY-01) and the
 * drive-by-drive scoring summary (FR-SUMMARY-02, FR-SUMMARY-03).
 *
 * Both read `statistics.general` / `statistics.scoring_summary` straight from
 * `GET /stats/v1/summary/{event_id}`. Quarters above 4 are labelled OT1, OT2, …
 * — the aggregate emits as many columns as the game actually played, so a
 * double-overtime game widens the table instead of truncating.
 *
 * The drive table's filters are lifted to the page so they can live in the URL
 * and survive a refresh (a sideline tablet reloads more than you would like).
 */
import type { GameResult, QuarterScore, ScoringDrive, SummaryGeneral } from '../../lib/api-types';

const RESULT_LABELS: Record<GameResult, string> = { win: 'WIN', loss: 'LOSS', tie: 'TIE' };

const POSSESSION_LABELS: Record<string, string> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

/** Q1…Q4, then OT1, OT2, … */
export function quarterLabel(quarter: number): string {
  return quarter <= 4 ? `Q${quarter}` : `OT${quarter - 4}`;
}

/** Own-half / opponent-half yard line as a coach says it: "own 35", "opp 12". */
export function yardLineLabel(yardLine: number | null): string {
  if (yardLine === null) return '—';
  if (yardLine === 50) return '50';
  return yardLine < 50 ? `own ${yardLine}` : `opp ${100 - yardLine}`;
}

function teamScoreOf(quarter: QuarterScore, isHome: boolean): number {
  return isHome ? quarter.home_score : quarter.away_score;
}

export interface ScoreByQuarterProps {
  general: SummaryGeneral;
  /** Our own team's display name; the tenant portal is one team. */
  teamName: string;
}

export function ScoreByQuarter({ general, teamName }: ScoreByQuarterProps): JSX.Element {
  const quarters = general.score_by_quarter;
  const opponentName = general.opponent_name ?? 'Opponent';
  const result = general.result;

  return (
    <section className="line-score" data-testid="score-by-quarter" aria-label="Score by quarter">
      <header className="line-score__header">
        <h2 className="line-score__title">Score</h2>
        {result === null ? (
          <span className="line-score__result" data-result="pending">
            IN PROGRESS
          </span>
        ) : (
          <span className="line-score__result" data-result={result} data-testid="game-result">
            {RESULT_LABELS[result]}
          </span>
        )}
      </header>

      <div className="line-score__body">
        <div className="line-score__scroller">
          <table className="line-score__table">
            <caption className="visually-hidden">
              Score by quarter with the running total for {teamName} and {opponentName}
            </caption>
            <thead>
              <tr>
                <th scope="col">Team</th>
                {quarters.map((quarter) => (
                  <th scope="col" key={quarter.quarter}>
                    {quarterLabel(quarter.quarter)}
                  </th>
                ))}
                <th scope="col" className="line-score__total-head">
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="line-score-team">
                <th scope="row">
                  {teamName}
                  <span className="line-score__venue">{general.is_home ? 'home' : 'away'}</span>
                </th>
                {quarters.map((quarter) => (
                  <td className="num" key={quarter.quarter}>
                    {teamScoreOf(quarter, general.is_home)}
                  </td>
                ))}
                <td className="num line-score__total" data-testid="line-score-team-total">
                  {general.total_score.team}
                </td>
              </tr>
              <tr data-testid="line-score-opponent">
                <th scope="row">
                  {opponentName}
                  <span className="line-score__venue">{general.is_home ? 'away' : 'home'}</span>
                </th>
                {quarters.map((quarter) => (
                  <td className="num" key={quarter.quarter}>
                    {teamScoreOf(quarter, !general.is_home)}
                  </td>
                ))}
                <td className="num line-score__total" data-testid="line-score-opponent-total">
                  {general.total_score.opponent}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <dl className="line-score__meta" data-testid="game-state">
          <div>
            <dt>Quarter</dt>
            <dd className="num">{quarterLabel(general.quarter)}</dd>
          </div>
          <div>
            <dt>Clock</dt>
            <dd className="num" data-testid="game-clock">
              {general.game_clock ?? '—'}
            </dd>
          </div>
          <div>
            <dt>Possession</dt>
            <dd data-testid="possession">
              {general.possession === null ? '—' : POSSESSION_LABELS[general.possession]}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

export type DriveScopeFilter = 'all' | 'team' | 'opponent';

export interface DriveFilters {
  /** Show only drives that produced points. */
  isScoringOnly: boolean;
  scope: DriveScopeFilter;
}

export const DEFAULT_DRIVE_FILTERS: DriveFilters = { isScoringOnly: false, scope: 'all' };

export function filterDrives(
  drives: readonly ScoringDrive[],
  filters: DriveFilters
): ScoringDrive[] {
  return drives.filter((drive) => {
    if (filters.isScoringOnly && drive.points <= 0) return false;
    if (filters.scope !== 'all' && drive.possession !== filters.scope) return false;
    return true;
  });
}

export interface ScoringSummaryProps {
  drives: readonly ScoringDrive[];
  teamName: string;
  opponentName: string;
  filters: DriveFilters;
  onFiltersChange: (filters: DriveFilters) => void;
  isLoading?: boolean;
}

export function ScoringSummary({
  drives,
  teamName,
  opponentName,
  filters,
  onFiltersChange,
  isLoading = false,
}: ScoringSummaryProps): JSX.Element {
  const visible = filterDrives(drives, filters);
  const scopeLabel = (drive: ScoringDrive): string =>
    drive.possession === 'team' ? teamName : opponentName;

  return (
    <section
      className="drive-summary"
      data-testid="scoring-summary"
      aria-label="Scoring summary by drive"
    >
      <header className="drive-summary__header">
        <h2 className="drive-summary__title">Scoring Summary</h2>
        <div className="drive-summary__filters">
          <label className="drive-summary__filter">
            <span>Drives</span>
            <select
              value={filters.scope}
              data-testid="drive-scope-filter"
              onChange={(changeEvent) =>
                onFiltersChange({
                  ...filters,
                  scope: changeEvent.target.value as DriveScopeFilter,
                })
              }
            >
              <option value="all">All drives</option>
              <option value="team">{teamName}</option>
              <option value="opponent">{opponentName}</option>
            </select>
          </label>
          <label className="drive-summary__toggle">
            <input
              type="checkbox"
              checked={filters.isScoringOnly}
              data-testid="drive-scoring-only"
              onChange={(changeEvent) =>
                onFiltersChange({ ...filters, isScoringOnly: changeEvent.target.checked })
              }
            />
            <span>Scoring drives only</span>
          </label>
        </div>
      </header>

      {isLoading && drives.length === 0 ? (
        <p className="drive-summary__empty" role="status">
          Loading drives…
        </p>
      ) : visible.length === 0 ? (
        <p className="drive-summary__empty" data-testid="scoring-summary-empty">
          {drives.length === 0
            ? 'No drives logged yet. Each drive appears here as plays are entered.'
            : 'No drives match these filters. Clear them to see the full game.'}
        </p>
      ) : (
        <div className="drive-summary__scroller">
          <table className="drive-summary__table">
            <caption className="visually-hidden">
              Every drive in possession order with its scoring result
            </caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Qtr</th>
                <th scope="col">Team</th>
                <th scope="col">Start</th>
                <th scope="col">Plays</th>
                <th scope="col">Yds</th>
                <th scope="col">TOP</th>
                <th scope="col">Result</th>
                <th scope="col">Pts</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((drive) => (
                <tr
                  key={drive.drive}
                  data-testid={`drive-row-${drive.drive}`}
                  data-scored={drive.points > 0}
                >
                  <td className="num">{drive.drive}</td>
                  <td>{quarterLabel(drive.quarter)}</td>
                  <td title={scopeLabel(drive)}>{scopeLabel(drive)}</td>
                  <td>{yardLineLabel(drive.start_yard_line)}</td>
                  <td className="num">{drive.plays}</td>
                  <td className="num">{drive.yards}</td>
                  <td className="num">{drive.time_of_possession ?? '—'}</td>
                  <td>{drive.result}</td>
                  <td className="num">{drive.points === 0 ? '—' : drive.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="drive-summary__count">
        {visible.length} of {drives.length} {drives.length === 1 ? 'drive' : 'drives'}
      </p>
    </section>
  );
}
